// Self-contained barcode encoders — EAN-13 (book ISBN barcodes) and Code 128
// (library control numbers: card numbers, OCLC, LCCN, accession numbers).
// PORTED VERBATIM from shippra apps/libms/src/lib/cards/barcode.ts (the library
// cards + spine-label feature). No dependencies; each encoder returns a flat
// array of module bits (1 = bar, 0 = space) that a renderer paints as <rect>
// runs. PURE — no DOM — so it typechecks in the worker lane and is testable in
// node (test/cards.test.ts).

export type Symbology = 'ean13' | 'code128' | 'issn' | 'auto';

export interface BarcodeData {
  bits: number[];
  text: string;
  symbology: 'ean13' | 'code128';
}

/* ----------------------------------- EAN-13 ----------------------------------- */

// Left-hand digit encodings (odd parity = L, even parity = G) and right-hand (R).
const EAN_L = ['0001101', '0011001', '0010011', '0111101', '0100011', '0110001', '0101111', '0111011', '0110111', '0001011'];
const EAN_G = ['0100111', '0110011', '0011011', '0100001', '0011101', '0111001', '0000101', '0010001', '0001001', '0010111'];
const EAN_R = ['1110010', '1100110', '1101100', '1000010', '1011100', '1001110', '1010000', '1000100', '1001000', '1110100'];
// First-digit parity pattern for the six left-hand digits.
const EAN_PARITY = ['LLLLLL', 'LLGLGG', 'LLGGLG', 'LLGGGL', 'LGLLGG', 'LGGLLG', 'LGGGLL', 'LGLGLG', 'LGLGGL', 'LGGLGL'];

/** Mod-10 check digit for the first 12 digits of an EAN-13 / ISBN-13. */
export function ean13Check(d12: string): string {
  let sum = 0;
  for (let i = 0; i < 12; i++) sum += Number(d12[i]) * (i % 2 === 0 ? 1 : 3);
  return String((10 - (sum % 10)) % 10);
}

/** Convert an ISSN to its 13-digit journal barcode (EAN-13: "977" + 7 ISSN digits + "00" variant + check). */
export function issnToEan13(issn: string): string | null {
  const d = (issn || '').replace(/[^0-9Xx]/g, '');
  if (d.length < 8) return null;
  const core = d.slice(0, 7); // first 7 ISSN digits, dropping the ISSN's own check digit
  if (!/^\d{7}$/.test(core)) return null;
  const base = '977' + core + '00';
  return base + ean13Check(base);
}

/** Encode a 12- or 13-digit value as an EAN-13 module array, or null if not numeric. */
export function encodeEAN13(value: string): number[] | null {
  let d = (value || '').replace(/[^0-9]/g, '');
  if (d.length === 12) d += ean13Check(d);
  if (d.length !== 13) return null;
  const first = Number(d[0]);
  const left = d.slice(1, 7);
  const right = d.slice(7, 13);
  const parity = EAN_PARITY[first] as string;
  let s = '101'; // start guard
  for (let i = 0; i < 6; i++) s += (parity[i] === 'L' ? EAN_L : EAN_G)[Number(left[i])] as string;
  s += '01010'; // centre guard
  for (let i = 0; i < 6; i++) s += EAN_R[Number(right[i])] as string;
  s += '101'; // end guard
  return s.split('').map(Number);
}

/* ---------------------------------- Code 128 ---------------------------------- */

// Module-width patterns for symbol values 0-106 (bar,space,bar,space,bar,space);
// index 106 is the 13-module Stop pattern. 103/104/105 are Start A/B/C.
const CODE128 = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212', '221213',
  '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221', '223211', '221132',
  '221231', '213212', '223112', '312131', '311222', '321122', '321221', '312212', '322112', '322211',
  '212123', '212321', '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121', '313121', '211331',
  '231131', '213113', '213311', '213131', '311123', '311321', '331121', '312113', '312311', '332111',
  '314111', '221411', '431111', '111224', '111422', '121124', '121421', '141122', '141221', '112214',
  '112412', '122114', '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
  '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311', '113141',
  '114131', '311141', '411131', '211412', '211214', '211232', '2331112',
];

// Code 128 subset switching. Code C packs two digits into one symbol, so a
// numeric run encodes in half the modules — and module count is what decides
// whether a barcode is physically readable on a small label.
//
// The original port entered Code C only when the WHOLE value was an even-length
// digit run, which made encoding depend on parity rather than content: the
// 15-digit barcodes this ERP mints ('310002795773700') fell back to pure Code B
// at 200 modules, while ADDING a digit dropped them to 123. On the 2x1in roll
// that is 1.60 dots per module against a 2-dot floor at 203 dpi — printed, and
// unscannable. Proper switching brings the same value to 134 modules (2.39
// dots). The output is ordinary conformant Code 128; every scanner decodes the
// subset switches natively.
const CODE_C = 99;
const CODE_B = 100;
const START_B = 104;
const START_C = 105;

/** Length of the run of ASCII digits starting at `i`. */
function digitRun(v: string, i: number): number {
  let n = 0;
  while (i + n < v.length) {
    const c = v.charCodeAt(i + n);
    if (c < 48 || c > 57) break;
    n++;
  }
  return n;
}

/**
 * Is entering Code C at `i` worth the switch symbol? Four digits pay for it at
 * the start (the start character is free) or at the tail; mid-value it takes
 * six. An odd run yields its first digit to Code B so the remainder is
 * pair-aligned — that single character is what the old gate never did.
 */
function entersCodeC(v: string, i: number): boolean {
  const n = digitRun(v, i);
  if (n < 2) return false;
  if (i + n === v.length && n % 2 === 0) return n >= 4 || i === 0;
  return i === 0 ? n >= 4 : n >= 6;
}

/** Encode arbitrary printable-ASCII text as a Code 128 module array (subset-switching). */
export function encodeCode128(value: string): number[] | null {
  const v = (value || '').trim();
  if (!v) return null;
  for (let k = 0; k < v.length; k++) {
    const c = v.charCodeAt(k);
    if (c < 32 || c > 126) return null; // Code A/B printable ASCII only
  }

  const data: number[] = [];
  let i = 0;
  let inC = entersCodeC(v, 0) && digitRun(v, 0) % 2 === 0;
  const start = inC ? START_C : START_B;

  while (i < v.length) {
    if (inC) {
      if (digitRun(v, i) >= 2) {
        data.push(parseInt(v.slice(i, i + 2), 10));
        i += 2;
      } else {
        data.push(CODE_B);
        inC = false;
      }
    } else if (entersCodeC(v, i)) {
      if (digitRun(v, i) % 2 === 1) {
        data.push(v.charCodeAt(i) - 32); // spend one digit to reach pair alignment
        i++;
      } else {
        data.push(CODE_C);
        inC = true;
      }
    } else {
      data.push(v.charCodeAt(i) - 32);
      i++;
    }
  }

  let sum = start;
  data.forEach((d, i) => { sum += d * (i + 1); });
  const symbols = [start, ...data, sum % 103, 106];

  const bits: number[] = [];
  for (const s of symbols) {
    const pat = CODE128[s] as string;
    for (let j = 0; j < pat.length; j++) {
      const w = Number(pat[j]);
      const bit = j % 2 === 0 ? 1 : 0;
      for (let k = 0; k < w; k++) bits.push(bit);
    }
  }
  return bits;
}

/* ----------------------------------- Public ----------------------------------- */

/** Build barcode bits + caption for a value, auto-selecting EAN-13 for 13-digit ISBNs. */
export function makeBarcode(value: string, symbology: Symbology = 'auto'): BarcodeData | null {
  const v = (value || '').trim();
  if (!v) return null;
  const digits = v.replace(/[^0-9]/g, '');

  if (symbology === 'issn') {
    const ean = issnToEan13(v);
    if (!ean) return null;
    const bits = encodeEAN13(ean);
    return bits ? { bits, text: v, symbology: 'ean13' } : null;
  }

  if (symbology === 'ean13' || (symbology === 'auto' && (digits.length === 13 || digits.length === 12))) {
    const bits = encodeEAN13(digits);
    if (bits) return { bits, text: digits.length === 12 ? digits + ean13Check(digits) : digits, symbology: 'ean13' };
    if (symbology === 'ean13') return null;
  }

  const bits = encodeCode128(v);
  if (!bits) return null;
  return { bits, text: v, symbology: 'code128' };
}
